/**
 * Reusable CSV export utility.
 *
 * ## Quick start
 *
 * ```ts
 * import { downloadCsv, formatIsoDate, formatRichText } from 'utils/csvExport';
 *
 * downloadCsv(
 *   rows,
 *   [
 *     { key: 'changeType', header: 'Change type' },
 *     { key: 'changeDate', header: 'Change date', format: formatIsoDate },
 *     { key: 'section',    header: 'Section' },
 *     { key: 'phase',      header: 'Phase' },
 *     { key: 'item',       header: 'Item' },
 *     {
 *       key: 'product',
 *       header: 'Product',
 *       value: row => row.feature?.product ?? ''
 *     },
 *     { key: 'from', header: 'From', format: formatRichText },
 *     { key: 'to',   header: 'To',   format: formatRichText }
 *   ],
 *   { filename: `changelog-${momentId}-${today}` }
 * );
 * ```
 *
 * ## Architecture
 *
 * - {@link buildCsv} — pure string builder; DOM-free, unit-tested in isolation.
 * - {@link downloadCsv} — browser file-download wrapper around `buildCsv`.
 * - {@link csvEscape} / {@link guardFormulaInjection} / {@link truncateForExcel} — low-level
 *   primitives used by the builder and exposed for advanced callers.
 * - {@link htmlToPlainText} — converts rich-text HTML to structured plain text (bullets, line
 *   breaks, hyperlink URLs). Reused by the `formatRichText` column formatter.
 * - {@link formatIsoDate} / {@link formatLocaleDate} / {@link formatJsonBlob} /
 *   {@link formatRichText} / {@link composeFormatters} — off-the-shelf column formatters.
 */

export { buildCsv } from './buildCsv';
export { downloadCsv, ensureCsvExtension, isIOS } from './downloadCsv';
export { copyCsvToClipboard } from './copyCsv';
export type { CopyCsvOptions } from './copyCsv';
export {
  csvEscape,
  guardFormulaInjection,
  truncateForExcel,
  EXCEL_CELL_CHAR_LIMIT
} from './csvEscape';
export { htmlToPlainText } from './htmlToPlainText';
export {
  formatIsoDate,
  formatIsoDateTime,
  formatLocaleDate,
  formatRichText,
  formatJsonBlob,
  composeFormatters
} from './formatters';
export type {
  CellFormatter,
  ColumnDef,
  BuildCsvOptions,
  DownloadCsvOptions
} from './types';
